import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Loader2, Plug } from 'lucide-react';
import { toast } from 'sonner';
import { mcpApi } from '@/api/mcp';
import { http, unwrapError } from '@/api/client';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import type { McpConfigView } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';

export function McpTab(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId, currentOrg } = useWorkspace();
  const callerRole = currentOrg?.role ?? null;
  const canManage = callerRole === 'owner' || callerRole === 'admin';

  const query = useQuery({
    queryKey: ['org', 'mcp', currentBotId],
    queryFn: () => mcpApi.get(currentBotId!),
    enabled: !!currentBotId,
  });

  const updateMutation = useMutation({
    mutationFn: (patch: { enabled: boolean }) => mcpApi.update(currentBotId!, patch),
    onSuccess: () => {
      toast.success(t('organization.mcp.savedToast', { defaultValue: 'MCP configuration saved' }));
      void queryClient.invalidateQueries({ queryKey: ['org', 'mcp', currentBotId] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  if (!currentBotId) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          {t('organization.mcp.selectBot', { defaultValue: 'Select a bot to configure its MCP server.' })}
        </CardContent>
      </Card>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          {t('common.loading')}
        </CardContent>
      </Card>
    );
  }

  if (query.error || !query.data) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">{unwrapError(query.error)}</CardContent>
      </Card>
    );
  }

  const config = query.data;

  return (
    <div className="space-y-6">
      <ActivationCard
        config={config}
        canManage={canManage}
        onToggle={(enabled) => updateMutation.mutate({ enabled })}
        pending={updateMutation.isPending}
      />
      <ConnectionSnippetCard enabled={config.enabled} />
    </div>
  );
}

function ActivationCard({
  config,
  canManage,
  onToggle,
  pending,
}: {
  config: McpConfigView;
  canManage: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {t('organization.mcp.activation.title', { defaultValue: 'Activation' })}
          </CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('organization.mcp.activation.subtitle', {
              defaultValue:
                'Expose this bot as an MCP server. Requires a Bearer key with scope "mcp", minted from the API keys tab.',
            })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={config.enabled ? 'success' : 'outline'} className="text-[10px]">
            {config.enabled
              ? t('organization.mcp.activation.on', { defaultValue: 'Enabled' })
              : t('organization.mcp.activation.off', { defaultValue: 'Disabled' })}
          </Badge>
          <Switch checked={config.enabled} disabled={!canManage || pending} onCheckedChange={onToggle} />
        </div>
      </CardHeader>
    </Card>
  );
}

function ConnectionSnippetCard({ enabled }: { enabled: boolean }): JSX.Element {
  const { t } = useTranslation();
  const apiBase = http.defaults.baseURL || 'https://zorro-api.upela.com';
  const mcpUrl = `${apiBase}/v1/mcp`;
  const keyPlaceholder = '<PASTE_MCP_API_KEY>';

  const claudeCodeSnippet = `claude mcp add --transport http zorro -- ${mcpUrl} --header "Authorization: Bearer ${keyPlaceholder}"`;

  const geminiCliSnippet = `{
  "mcpServers": {
    "zorro": {
      "httpUrl": "${mcpUrl}",
      "headers": { "Authorization": "Bearer ${keyPlaceholder}" }
    }
  }
}`;

  const claudeDesktopSnippet = `{
  "mcpServers": {
    "zorro": {
      "url": "${mcpUrl}",
      "headers": { "Authorization": "Bearer ${keyPlaceholder}" }
    }
  }
}`;

  const copy = (text: string): void => {
    void navigator.clipboard.writeText(text);
    toast.success(t('organization.mcp.snippet.copied', { defaultValue: 'Snippet copied' }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="h-4 w-4" />
          {t('organization.mcp.snippet.title', { defaultValue: 'Connection snippet' })}
        </CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('organization.mcp.snippet.subtitle', {
            defaultValue:
              'Replace {{placeholder}} with an mcp-scoped key minted from the API keys tab. These snippets never embed a real key.',
            placeholder: keyPlaceholder,
          })}
        </p>
        {!enabled ? (
          <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
            {t('organization.mcp.snippet.disabledWarning', {
              defaultValue: 'MCP is currently disabled for this bot — enable it above before connecting a client.',
            })}
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        <SnippetBlock
          label={t('organization.mcp.snippet.claudeCode', { defaultValue: 'Claude Code' })}
          snippet={claudeCodeSnippet}
          onCopy={() => copy(claudeCodeSnippet)}
        />
        <SnippetBlock
          label={t('organization.mcp.snippet.geminiCli', { defaultValue: 'Gemini CLI (settings.json)' })}
          snippet={geminiCliSnippet}
          onCopy={() => copy(geminiCliSnippet)}
        />
        <SnippetBlock
          label={t('organization.mcp.snippet.claudeDesktop', { defaultValue: 'Claude Desktop (config file)' })}
          snippet={claudeDesktopSnippet}
          onCopy={() => copy(claudeDesktopSnippet)}
        />
      </CardContent>
    </Card>
  );
}

function SnippetBlock({
  label,
  snippet,
  onCopy,
}: {
  label: string;
  snippet: string;
  onCopy: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <details className="rounded-md border bg-muted/20 p-3 text-xs" open>
      <summary className="cursor-pointer font-medium">{label}</summary>
      <pre className="mt-2 overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">
        {snippet}
      </pre>
      <Button variant="outline" size="sm" className="mt-2 gap-2" onClick={onCopy}>
        <Copy className="h-3.5 w-3.5" />
        {t('common.copy', { defaultValue: 'Copy' })}
      </Button>
    </details>
  );
}
